import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Layout, Menu, Dropdown, Avatar, Space, Typography, Input, theme, Divider, Button, Tooltip, Switch, ConfigProvider, Modal, List, Tag, Card, Progress, message } from 'antd';
import { 
  UserOutlined, 
  LogoutOutlined, 
  SearchOutlined,
  QuestionCircleOutlined,
  MoonOutlined,
  SunOutlined,
  MenuOutlined,
  SettingOutlined,
  MessageOutlined,
  DashboardOutlined,
  TeamOutlined,
  FileTextOutlined,
  ZoomInOutlined,
  ShoppingOutlined,
  UserSwitchOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  MailOutlined,
  BarcodeOutlined,
  TruckOutlined,
  ClockCircleOutlined,
  WifiOutlined,
  DisconnectOutlined,
  SyncOutlined,
  CloudServerOutlined,
  HddOutlined,
  DownOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import './Header.css';
import config from '../config';
import NotificationBell from './NotificationBell';
import InstallPrompt from './InstallPrompt';
const { Header } = Layout;
const { Text, Title } = Typography;
const { Search } = Input;

const CustomHeader = ({ 
  userName = 'John Doe', 
  userRole = 'Administrator',
  userAvatar,
  handleLogout, 
  darkTheme = false, 
  toggleTheme,
  onMenuClick,
  orders = [],
  isMobile,
  /* ── Existing app state reused by the header status bar. All optional so the
     header keeps rendering even when a value is not supplied. ── */
  lastSyncTime,           // App.js `lastFetchTime` – real time of the last orders fetch
  onRefresh,              // App.js `refetch`     – the app's existing refresh action
  refreshing = false,     // App.js `isFetching`
  isSocketConnected,      // App.js `isSocketConnected` – live backend link (WebSocket)
  odooStatus,             // optional – real Odoo status once a source is available
  ramUsage,               // optional – real RAM metrics once a source is available
  weather,                // optional – real weather data once a source is available
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchVisible, setSearchVisible] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchModalVisible, setIsSearchModalVisible] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [hasRecentActivity, setHasRecentActivity] = useState(true);
  const [lastActivityTime, setLastActivityTime] = useState(new Date());
  const [liveWeather, setLiveWeather] = useState(null);
  const activityStateRef = useRef(true);
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchSpinning, setSearchSpinning] = useState(false);
  const [typewriterText, setTypewriterText] = useState('');
  const [typewriterIndex, setTypewriterIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  // Typewriter placeholder suggestions
  const typewriterSuggestions = useMemo(() => [
    'Search across Packing Console...',
    'Search across Packing Console...',
    'Search across Packing Console...',
    'Search across Packing Console...',
    'Search across Packing Console...',
  ], []);

  // Typewriter effect
  useEffect(() => {
    if (searchFocused || searchValue) {
      setTypewriterText('');
      return;
    }

    const currentSuggestion = typewriterSuggestions[typewriterIndex];
    let timeout;

    if (!isDeleting) {
      // Typing phase
      if (typewriterText.length < currentSuggestion.length) {
        timeout = setTimeout(() => {
          setTypewriterText(currentSuggestion.slice(0, typewriterText.length + 1));
        }, 40 + Math.random() * 30);
      } else {
        // Pause before deleting
        timeout = setTimeout(() => setIsDeleting(true), 2000);
      }
    } else {
      // Deleting phase
      if (typewriterText.length > 0) {
        timeout = setTimeout(() => {
          setTypewriterText(typewriterText.slice(0, -1));
        }, 20);
      } else {
        // Move to next suggestion
        setIsDeleting(false);
        setTypewriterIndex((prev) => (prev + 1) % typewriterSuggestions.length);
      }
    }

    return () => clearTimeout(timeout);
  }, [typewriterText, isDeleting, typewriterIndex, searchFocused, searchValue, typewriterSuggestions]);

  const {
    token: { colorBgContainer, colorTextBase, colorPrimary, colorBorderSecondary, colorSuccess, colorWarning, colorError }
  } = theme.useToken();

  /* ────────────────────────────────────────────────────────────────────────
     GLOBAL HEADER STATUS BAR – display only
     - Version / environment come from the build (CRA injects NODE_ENV), so no
       fake status is shown and nothing here talks to the backend.
     - RAM, weather and Odoo currently have NO data source in the frontend.
       They therefore render a placeholder that can be replaced without any
       refactor by simply passing the matching prop (ramUsage / weather /
       odooStatus) from the parent – no new API call is introduced here.
     ──────────────────────────────────────────────────────────────────────── */
  const APP_VERSION = 'v5.0.0';
  const APP_ENV = process.env.NODE_ENV === 'production' ? 'Production' : 'Development';

  const RAM_PLACEHOLDER = { usedGb: 5.2, totalGb: 16 };              // TODO: wire to real metrics
  const WEATHER_PLACEHOLDER = { temp: null, city: 'Vijayawada', icon: '☀️' };
  const ODOO_PLACEHOLDER = { connected: true, label: 'Connected' };   // TODO: wire to the real Odoo status

  const ram = ramUsage || RAM_PLACEHOLDER;
  const wx = weather || liveWeather || WEATHER_PLACEHOLDER;
  const odoo = odooStatus
    ? {
        connected: !!odooStatus.connected,
        label: odooStatus.label || (odooStatus.connected ? 'Connected' : 'Disconnected'),
      }
    : ODOO_PLACEHOLDER;

  const ramPercent =
    ram.totalGb > 0
      ? Math.max(0, Math.min(100, Math.round((ram.usedGb / ram.totalGb) * 100)))
      : 0;
  const ramTone = ramPercent >= 90 ? 'bad' : ramPercent >= 70 ? 'warn' : 'ok';

  // The header represents whether the user is actively using this page. A
  // disconnected optional socket should not mark an otherwise active browser
  // session as offline.
  const systemOnline = isOnline && hasRecentActivity;

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Mark the page online while the user is interacting with it and offline
  // after one minute without activity or while the tab is hidden.
  useEffect(() => {
    let idleTimer;
    const markActive = () => {
      if (!activityStateRef.current) {
        activityStateRef.current = true;
        setHasRecentActivity(true);
        setLastActivityTime(new Date());
      }
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        activityStateRef.current = false;
        setHasRecentActivity(false);
        setLastActivityTime(new Date());
      }, 60 * 1000);
    };
    const handleVisibility = () => {
      if (document.hidden) {
        clearTimeout(idleTimer);
        activityStateRef.current = false;
        setHasRecentActivity(false);
        setLastActivityTime(new Date());
      } else {
        markActive();
      }
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach((eventName) => window.addEventListener(eventName, markActive, { passive: true }));
    document.addEventListener('visibilitychange', handleVisibility);
    markActive();
    return () => {
      clearTimeout(idleTimer);
      events.forEach((eventName) => window.removeEventListener(eventName, markActive));
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  // Vijayawada weather from Open-Meteo; no API key is required.
  useEffect(() => {
    let cancelled = false;
    const loadWeather = async () => {
      try {
        const response = await fetch(
          'https://api.open-meteo.com/v1/forecast?latitude=16.5062&longitude=80.6480&current=temperature_2m,weather_code&timezone=Asia%2FKolkata'
        );
        if (!response.ok) throw new Error('Weather request failed');
        const data = await response.json();
        if (cancelled || !data.current) return;
        const code = Number(data.current.weather_code);
        const icon = code === 0 ? '☀️' : code <= 3 ? '🌤️' : code <= 67 ? '🌧️' : code <= 77 ? '❄️' : '⛈️';
        setLiveWeather({ temp: Math.round(data.current.temperature_2m), city: 'Vijayawada', icon });
      } catch (error) {
        console.warn('Weather fetch failed:', error);
      }
    };
    loadWeather();
    const weatherTimer = setInterval(loadWeather, 10 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(weatherTimer);
    };
  }, []);

  // Handle network status
  useEffect(() => {
    const updateOnlineStatus = () => setIsOnline(navigator.onLine);
    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);
    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
    };
  }, []);

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get greeting based on time
  const getGreeting = () => {
    const currentHour = new Date().getHours();
    if (currentHour < 12) return 'Good Morning';
    if (currentHour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const getTimeIcon = () => {
    const currentHour = new Date().getHours();
    if (currentHour < 12) return '🌅';
    if (currentHour < 17) return '☀️';
    return '🌙';
  };

  // Handle search functionality
  const handleSearch = (value) => {
    setSearchValue(value);
    
    if (!value.trim()) {
      setSearchResults([]);
      setIsSearchModalVisible(false);
      return;
    }

    // Trigger spin animation
    setSearchSpinning(true);
    setTimeout(() => setSearchSpinning(false), 600);

    const searchTerm = value.toLowerCase().trim();
    
    const results = orders.filter(order => {
      // Search by order ID
      if (order.orderID?.toLowerCase().includes(searchTerm)) return true;
      
      // Search by customer name
      if (order.customer?.name?.toLowerCase().includes(searchTerm)) return true;
      
      // Search by customer email
      if (order.customer?.email?.toLowerCase().includes(searchTerm)) return true;
      
      // Search by customer mobile
      if (order.customer?.mobile?.includes(searchTerm)) return true;
      
      // Search by product name
      if (order.products?.some(product => 
        product.name?.toLowerCase().includes(searchTerm) ||
        product.sku?.toLowerCase().includes(searchTerm)
      )) return true;
      
      // Search by AWB code
      if (order.shipments?.some(shipment => 
        shipment.awb_code?.toLowerCase().includes(searchTerm)
      )) return true;

      return false;
    });

    setSearchResults(results);
    setIsSearchModalVisible(true);
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleSignOut = () => {
    handleLogout?.();
    navigate('/signin');
  };

  // Enhanced Search Results Modal (your existing code)
  const SearchResultsModal = () => (
    <Modal
      title={
        <Space align="center">
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              backgroundColor: colorPrimary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white'
            }}
          >
            <SearchOutlined />
          </div>
          <Space direction="vertical" size={0}>
            <Text strong style={{ fontSize: '16px', color: colorTextBase }}>
              Search Results
            </Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Found {searchResults.length} order{searchResults.length !== 1 ? 's' : ''} for "{searchValue}"
            </Text>
          </Space>
        </Space>
      }
      open={isSearchModalVisible}
      onCancel={() => setIsSearchModalVisible(false)}
      footer={null}
      width={900}
      className="search-results-modal-enhanced"
      style={{ top: 20 }}
      styles={{
        body: {
          padding: '0 24px 24px 24px',
          maxHeight: '70vh',
          overflow: 'auto'
        }
      }}
    >
      {searchResults.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div 
            style={{ 
              width: 80, 
              height: 80, 
              borderRadius: '50%', 
              backgroundColor: darkTheme ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '32px',
              color: colorTextBase
            }}
          >
            <SearchOutlined />
          </div>
          <Title level={4} style={{ color: colorTextBase, marginBottom: 8 }}>
            No orders found
          </Title>
          <Text type="secondary" style={{ fontSize: '14px' }}>
            No orders matching "{searchValue}" were found. Try different keywords.
          </Text>
        </div>
      ) : (
        <div className="search-results-container">
          {searchResults.map((order, index) => (
            <Card
              key={order.orderID || index}
              className="order-card-enhanced"
              style={{
                marginBottom: 16,
                borderRadius: '12px',
                border: `1px solid ${colorBorderSecondary}`,
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              styles={{ body: { padding: '20px' } }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
              }}
              onClick={() => {
                // Navigate to order details or perform action
              }}
            >
              {/* Order Header */}
              <div className="order-header" style={{ marginBottom: 16 }}>
                <Space size="middle" align="start" style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Space size="middle">
                    <div 
                      style={{ 
                        width: 48, 
                        height: 48, 
                        borderRadius: '10px', 
                        backgroundColor: `${colorPrimary}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: `2px solid ${colorPrimary}30`
                      }}
                    >
                      <ShoppingOutlined style={{ fontSize: 20, color: colorPrimary }} />
                    </div>
                    <Space direction="vertical" size={2}>
                      <Space align="center">
                        <Text strong style={{ fontSize: '16px', color: colorTextBase }}>
                          {order.orderID}
                        </Text>
                        <Tag 
                          color={getStatusColor(order.packed_status)}
                          style={{ 
                            borderRadius: '6px',
                            fontWeight: 600,
                            border: 'none'
                          }}
                          icon={order.packed_status === 'Completed' ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
                        >
                          {order.packed_status}
                        </Tag>
                      </Space>
                      <Space size="small">
                        <CalendarOutlined style={{ fontSize: 12, color: colorTextBase }} />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {formatDate(order.order_date)} at {formatTime(order.order_date)}
                        </Text>
                      </Space>
                    </Space>
                  </Space>
                  
                  {/* Order Progress */}
                  <Space direction="vertical" size={4} style={{ textAlign: 'right' }}>
                    <Text strong style={{ fontSize: '12px', color: colorTextBase }}>
                      Order Progress
                    </Text>
                    <Progress 
                      percent={getStatusPercentage(order.packed_status)}
                      size="small" 
                      strokeColor={getStatusColor(order.packed_status)}
                      style={{ width: 120 }}
                      showInfo={false}
                    />
                    <Text type="secondary" style={{ fontSize: '10px' }}>
                      {getStatusPercentage(order.packed_status)}% Complete
                    </Text>
                  </Space>
                </Space>
              </div>

              {/* Customer Information */}
              <div className="customer-section" style={{ marginBottom: 16 }}>
                <Space align="start" size="middle" style={{ width: '100%' }}>
                  <Avatar 
                    size={36}
                    style={{ 
                      backgroundColor: `${colorPrimary}20`,
                      color: colorPrimary
                    }}
                    icon={<UserSwitchOutlined />}
                  />
                  <Space direction="vertical" size={2} style={{ flex: 1 }}>
                    <Text strong style={{ fontSize: '14px', color: colorTextBase }}>
                      {order.customer?.name}
                    </Text>
                    <Space size="middle">
                      <Space size="small">
                        <MailOutlined style={{ fontSize: 12, color: colorTextBase }} />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {order.customer?.email}
                        </Text>
                      </Space>
                      <Space size="small">
                        <PhoneOutlined style={{ fontSize: 12, color: colorTextBase }} />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {order.customer?.mobile}
                        </Text>
                      </Space>
                    </Space>
                  </Space>
                </Space>
              </div>

              {/* Products Section */}
              <div className="products-section" style={{ marginBottom: 16 }}>
                <Text strong style={{ fontSize: '14px', color: colorTextBase, marginBottom: 8, display: 'block' }}>
                  Products ({order.products?.length || 0})
                </Text>
                <div className="products-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8 }}>
                  {order.products?.map((product, productIndex) => (
                    <Card
                      key={productIndex}
                      size="small"
                      style={{
                        borderRadius: '8px',
                        border: `1px solid ${colorBorderSecondary}`,
                        backgroundColor: darkTheme ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'
                      }}
                      styles={{ body: { padding: '12px' } }}
                    >
                      <Space align="start" size={8} style={{ width: '100%' }}>
                        <div 
                          style={{ 
                            width: 32, 
                            height: 32, 
                            borderRadius: '6px', 
                            backgroundColor: `${colorPrimary}15`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                          }}
                        >
                          <BarcodeOutlined style={{ fontSize: 14, color: colorPrimary }} />
                        </div>
                        <Space direction="vertical" size={2} style={{ flex: 1, minWidth: 0 }}>
                          <Text strong style={{ fontSize: '12px', color: colorTextBase }} ellipsis={{ tooltip: product.name }}>
                            {product.name}
                          </Text>
                          <Space size={4}>
                            <Text type="secondary" style={{ fontSize: '11px' }}>
                              SKU: {product.sku}
                            </Text>
                            <Text type="secondary" style={{ fontSize: '11px' }}>
                              •
                            </Text>
                            <Text type="secondary" style={{ fontSize: '11px' }}>
                              Qty: {product.quantity}
                            </Text>
                          </Space>
                          {product.price && (
                            <Text strong style={{ fontSize: '12px', color: colorPrimary }}>
                              ${parseFloat(product.price).toFixed(2)}
                            </Text>
                          )}
                        </Space>
                      </Space>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Shipment Information */}
              {order.shipments?.length > 0 && (
                <div className="shipment-section">
                  <Text strong style={{ fontSize: '14px', color: colorTextBase, marginBottom: 8, display: 'block' }}>
                    Shipments ({order.shipments?.length || 0})
                  </Text>
                  <div className="shipments-list" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {order.shipments?.map((shipment, shipmentIndex) => (
                      <Card
                        key={shipmentIndex}
                        size="small"
                        style={{
                          borderRadius: '8px',
                          border: `1px solid ${colorBorderSecondary}`,
                          backgroundColor: darkTheme ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'
                        }}
                        styles={{ body: { padding: '12px' } }}
                      >
                        <Space align="start" size={12} style={{ width: '100%' }}>
                          <div 
                            style={{ 
                              width: 32, 
                              height: 32, 
                              borderRadius: '6px', 
                              backgroundColor: `${getStatusColor(shipment.status)}15`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}
                          >
                            <TruckOutlined style={{ fontSize: 14, color: getStatusColor(shipment.status) }} />
                          </div>
                          <Space direction="vertical" size={2} style={{ flex: 1 }}>
                            <Space size="middle">
                              <Text strong style={{ fontSize: '12px', color: colorTextBase }}>
                                {shipment.courier_name}
                              </Text>
                              <Tag 
                                color={getStatusColor(shipment.status)}
                                style={{ 
                                  borderRadius: '4px',
                                  fontSize: '10px',
                                  fontWeight: 600,
                                  border: 'none',
                                  margin: 0
                                }}
                              >
                                {shipment.status}
                              </Tag>
                            </Space>
                            <Space size="small">
                              <FileTextOutlined style={{ fontSize: 11, color: colorTextBase }} />
                              <Text type="secondary" style={{ fontSize: '11px', fontFamily: 'monospace' }}>
                                AWB: {shipment.awb_code}
                              </Text>
                            </Space>
                            {shipment.tracking_url && (
                              <Button 
                                type="link" 
                                size="small" 
                                style={{ padding: 0, height: 'auto', fontSize: '11px' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(shipment.tracking_url, '_blank');
                                }}
                              >
                                Track Shipment
                              </Button>
                            )}
                          </Space>
                        </Space>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Packing Information */}
              <div className="packing-section" style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${colorBorderSecondary}` }}>
                <Space size="middle" align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Space size="small">
                    <CheckCircleOutlined style={{ fontSize: 14, color: order.packed_status === 'Completed' ? colorSuccess : colorTextBase }} />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Packed: {order.packed_date ? formatDate(order.packed_date) : 'Not packed'}
                    </Text>
                    {order.packed_person_name && (
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        by {order.packed_person_name}
                      </Text>
                    )}
                  </Space>
                  <Button 
                    type="primary" 
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Handle order action
                    }}
                  >
                    View Details
                  </Button>
                </Space>
              </div>
            </Card>
          ))}
        </div>
      )}
    </Modal>
  );

  // Format time for display
  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'delivered':
        return colorSuccess;
      case 'shipped':
      case 'processing':
        return colorPrimary;
      case 'pending':
        return colorWarning;
      case 'cancelled':
      case 'failed':
        return colorError;
      default:
        return colorWarning;
    }
  };

  // Get status percentage for progress
  const getStatusPercentage = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 25;
      case 'processing': return 50;
      case 'shipped': return 75;
      case 'delivered': return 100;
      case 'completed': return 100;
      default: return 25;
    }
  };

  // USER MENU - This was missing!
  const userMenuItems = [
    {
      key: 'header',
      type: 'group',
      label: (
        <div className="user-menu-header-enhanced" style={{ padding: '16px 12px' }}>
          <Space align="start" size={12}>
            <Avatar
              size={48}
              className="user-avatar-enhanced"
              style={{
                backgroundColor: colorPrimary,
                border: `3px solid ${colorPrimary}20`
              }}
              src={userAvatar}
            >
              {userName ? userName.charAt(0).toUpperCase() : 'G'}
            </Avatar>
            <div className="user-info">
              <Title level={5} style={{ margin: 0, color: colorTextBase }}>
                {userName}
              </Title>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {userRole}
              </Text>
              <div style={{ marginTop: '4px' }}>
                <Text style={{ color: colorPrimary, fontSize: '12px' }}>
                  {getTimeIcon()} {getGreeting()}
                </Text>
              </div>
            </div>
          </Space>
        </div>
      ),
    },
    {
      key: 'dashboard',
      className: 'menu-item-enhanced',
      onClick: () => handleNavigation('/dashboard'),
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },   
    {
      key: 'settings',
      className: 'menu-item-enhanced',
      onClick: () => handleNavigation('/settings'),
      icon: <SettingOutlined />,
      label: 'Account Settings',
    },  
    {
      key: 'divider1',
      type: 'divider',
    },
    {
      key: 'theme-toggle',
      type: 'group',
      label: (
        <div className="menu-theme-toggle" style={{ padding: '8px 12px' }}>
          <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
            <Space>
              {darkTheme ? <MoonOutlined /> : <SunOutlined />}
              <Text>Dark Mode</Text>
            </Space>
            <Switch
              checked={darkTheme}
              onChange={toggleTheme}
              size="small"
            />
          </Space>
        </div>
      ),
    },
    {
      key: 'logout',
      className: 'menu-item-danger-enhanced',
      onClick: handleSignOut,
      icon: <LogoutOutlined />,
      label: 'Sign Out',
    },
  ];

  return (
    <>
      <Header
        className={`custom-header-enhanced ${scrolled ? 'scrolled' : ''} ${darkTheme ? 'dark-mode' : 'light-mode'}`}
        style={{
          background: colorBgContainer,
          borderBottom: `1px solid ${colorBorderSecondary}`,
          padding: isMobile ? '0 12px' : '0 24px',
          backdropFilter: 'blur(10px)',
          transition: 'all 0.3s ease',
          height: '64px',
          display: 'flex',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          width: '100%'
        }}
      >
        <div className="header-content-enhanced" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', height: '100%' }}>
          {/* Left Section — sidebar toggle, TGH logo, application branding.
              The toggle now renders on every screen size: onMenuClick is the
              existing useTheme().toggleSidebar, so it collapses/expands the
              desktop sidebar and opens the mobile drawer exactly as before. */}
          <div className="header-left" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {onMenuClick && (
              <div className="mobile-menu-btn" style={{ display: 'block' }}>
                <Tooltip title="Main Menu">
                  <Button 
                    type="text" 
                    icon={<MenuOutlined />} 
                    onClick={onMenuClick}
                    className="menu-btn-enhanced hdr-icon-btn hdr-menu-btn"
                    style={{ 
                      fontSize: '18px', 
                      color: colorTextBase,
                      width: 40,
                      height: 40,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  />
                </Tooltip>
              </div>
            )}
            
            <div className="header-brand-enhanced hdr-brand" onClick={() => navigate('/')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
              <div className="logo-container hdr-logo-wrap" style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                {/* Same asset and the same theme treatment the sidebar uses for it,
                    so the TGH mark stays visible in light and dark themes. */}
                {/* <img
                  src="/TGH%20Short%20Logo.webp"
                  alt="The Good Health"
                  className="hdr-logo"
                  style={{ filter: darkTheme ? 'none' : 'brightness(0)' }}
                /> */}
                {/* <div 
                  className="logo-icon"
                  style={{ 
                    backgroundColor: colorPrimary,
                    color: '#fff',
                    width: '32px',
                    height: '32px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '18px'
                  }}
                >
                  P
                </div> */}
                <div className="brand-text hdr-brand-text">
                  <span className="hdr-brand-title" style={{ color: colorTextBase }}>
                    <span className="hdr-title-full">TGH Packing Console</span>
                    <span className="hdr-title-short">Pack Console</span>
                  </span>
                  <span className="hdr-brand-meta">
                    <span className="hdr-version">{APP_VERSION}</span>
                    <span className="hdr-meta-dot" aria-hidden="true">•</span>
                    <span className={`hdr-env ${APP_ENV === 'Production' ? 'prod' : 'dev'}`}>
                      <span className="hdr-dot ok" aria-hidden="true" />
                      {APP_ENV}
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Center Section - Animated Search */}
          {!isMobile && (
          <div 
            className={`header-search-enhanced ${searchVisible ? 'visible' : ''} ${searchFocused ? 'expanded' : ''}`} 
            style={{ padding: '0 10px', display: 'flex', alignItems: 'center' }}
          >
            <Search
              placeholder={searchFocused || searchValue ? 'Search across Packing Console...' : 'Search across Packing Console...'}
              prefix={
                <SearchOutlined 
                  className={searchSpinning ? 'search-spin-icon' : ''} 
                  style={{ transition: 'color 0.3s ease', color: searchFocused ? '#4B5EAA' : undefined }}
                />
              }
              suffix={
                <Tooltip title="Press / to search">
                  <Text keyboard className="search-shortcut-badge" style={{ fontSize: '12px' }}>/</Text>
                </Tooltip>
              }
              className={`search-input-enhanced ${searchFocused ? 'typing-active' : ''}`}
              style={{
                background: darkTheme 
                  ? (searchFocused ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.05)') 
                  : (searchFocused ? 'rgba(255,255,255,1)' : 'rgba(0,0,0,0.03)'),
                borderRadius: '12px',
                transition: 'all 0.4s cubic-bezier(0.23, 1, 0.32, 1)',
              }}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onSearch={handleSearch}
              onPressEnter={(e) => handleSearch(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              allowClear
            />
          </div>
          )}

          {/* Status cluster — display only, every value comes from existing app state */}
          <div className="hdr-status-cluster">
            {/* System status + last sync (App.js lastFetchTime / refetch / isSocketConnected) */}
            <div className="hdr-chip hdr-system">
              <span className={`hdr-dot ${systemOnline ? 'ok live' : 'bad'}`} aria-hidden="true" />
              <div className="hdr-chip-text">
                <span className="hdr-stat-value">{systemOnline ? 'System Online' : 'System Offline'}</span>
                <span className="hdr-stat-sub">
                  {systemOnline
                    ? `Active now · ${formatTime(lastActivityTime)}`
                    : `Away since ${formatTime(lastActivityTime)}`}
                </span>
              </div>
              {onRefresh && (
                <Tooltip title="Refresh data">
                  <Button
                    type="text"
                    size="small"
                    className="hdr-icon-btn hdr-refresh-btn"
                    icon={<SyncOutlined spin={!!refreshing} />}
                    onClick={onRefresh}
                  />
                </Tooltip>
              )}
            </div>

            <span className="hdr-divider hdr-div-refresh" aria-hidden="true" />

            {/* Odoo connection status (placeholder until a real status source is passed in) */}
            <Tooltip title="Odoo connection status">
              <div className={`hdr-chip hdr-odoo ${odoo.connected ? 'ok' : 'bad'}`}>
                <CloudServerOutlined className="hdr-chip-icon" />
                <div className="hdr-chip-text">
                  <span className="hdr-stat-label">Odoo</span>
                  <span className="hdr-stat-value">
                    <span className={`hdr-dot ${odoo.connected ? 'ok' : 'bad'}`} aria-hidden="true" />
                    {odoo.label}
                  </span>
                </div>
              </div>
            </Tooltip>

            <span className="hdr-divider hdr-div-ram" aria-hidden="true" />

         

            <span className="hdr-divider hdr-div-wx" aria-hidden="true" />

            {/* Live Vijayawada weather */}
          
              <div className="hdr-chip hdr-weather">
                <span className="hdr-wx-icon" aria-hidden="true">{wx.icon}</span>
                <div className="hdr-chip-text">
                  <span className="hdr-stat-value">{wx.temp == null ? '—' : `${wx.temp}°C`}</span>
                  <span className="hdr-stat-sub">{wx.city}</span>
                </div>
              </div>
          
          </div>

          {/* Right Section */}
          <div className="header-actions-enhanced">
            <Space size="small" align="center">
              {isMobile && (
                <Button 
                  type="text" 
                  icon={<SearchOutlined />} 
                  onClick={() => setIsSearchModalVisible(true)}
                  style={{ color: colorTextBase }}
                />
              )}
              {/* Network Status — existing isOnline (navigator.onLine) state */}
              <Tooltip title={isOnline ? "Network online" : "Network offline"}>
                <Button 
                  type="text" 
                  icon={isOnline ? <WifiOutlined /> : <DisconnectOutlined />} 
                  className="action-icon-enhanced hdr-icon-btn hdr-net-btn"
                  style={{ 
                    color: isOnline ? colorSuccess : colorError,
                    backgroundColor: darkTheme ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)',
                    cursor: 'default'
                  }}
                />
              </Tooltip>
              <span className={`hdr-net-label ${isOnline ? 'ok' : 'bad'}`}>
                {isOnline ? 'Online' : 'Offline'}
              </span>
              {/* Theme Toggle */}
              <Tooltip title={darkTheme ? "Light Mode" : "Dark Mode"}>
                <Button 
                  type="text" 
                  icon={darkTheme ? <SunOutlined /> : <MoonOutlined />} 
                  onClick={toggleTheme}
                  className="action-icon-enhanced theme-toggle-btn hdr-icon-btn"
                  style={{ 
                    color: colorTextBase,
                    backgroundColor: darkTheme ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)'
                  }}
                />
              </Tooltip>
              
              <NotificationBell darkTheme={darkTheme} />
              <InstallPrompt />
              
              {/* User Profile */}
              <Dropdown
                menu={{ items: userMenuItems }}
                trigger={['click']}
                placement="bottomRight"
                overlayClassName="user-dropdown-enhanced"
              >
                <Space className="user-profile-enhanced" style={{ cursor: 'pointer', padding: '4px 8px', borderRadius: '8px' }}>
                  <Avatar 
                    size="default"
                    className="user-avatar-enhanced"
                    style={{ 
                      backgroundColor: colorPrimary,
                      border: `2px solid ${colorPrimary}30`
                    }}
                    src={userAvatar}
                  >
                    {userName ? userName.charAt(0).toUpperCase() : 'G'}
                  </Avatar>
                  {!isMobile && (
                    <div className="user-info-compact">
                      <Text strong className="username-enhanced" style={{ color: colorTextBase, fontSize: '14px' }}>
                        {userName.split(' ')[0]}
                        <DownOutlined className="hdr-caret" />
                      </Text>               
                    </div>
                  )}
                </Space>
              </Dropdown>
            </Space>
          </div>
        </div>
      </Header>

      {/* Enhanced Search Results Modal */}
      <SearchResultsModal />
    </>
  );
};

export default CustomHeader;
